import * as THREE from 'three';
import { Entity } from '../../../entities';
import type { Terrain, SurfaceObstacle } from '../../surface';
import type { FaunaSpec } from '../BiomeProfile';
import type { PlayerStats } from '../../player';
import { buildCreature } from './builders';
import type { CreatureMesh, CreatureColors } from './builders';

export interface CreatureOptions {
  readonly spec: FaunaSpec;
  readonly position: THREE.Vector3;
  readonly terrain: Terrain;
  /** Astronaut position provider — sampled every update so behaviour can
   *  react to the player. */
  readonly getPlayerPosition: () => THREE.Vector3;
  /** Boundary radius (XZ) — keeps fauna inside the playable disc. */
  readonly boundaryRadius: number;
  /** Pre-built materials shared with all creatures of the same species. */
  readonly colors: CreatureColors;
  /** Player stats for hostile-melee damage. Optional — passive worlds can
   *  omit this and predators will still wander but won't hurt anyone. */
  readonly stats?: PlayerStats | null;
  /** Player collision radius — used to size the no-overlap zone around
   *  the astronaut. Defaults to 0.36 (matches Astronaut). */
  readonly playerRadius?: number;
}

/**
 * Wandering surface creature. Animation rules of thumb:
 *
 *   - Body is built with forward = local +X (head on +X side).
 *   - Yaw aligns local +X with velocity via `atan2(-vz, vx)`.
 *   - Legs swing around their LOCAL Z axis (perpendicular to forward) so
 *     feet move forward/back, not sideways.
 *   - Each leg's gait phase comes from `userData.gaitPhase` set by the
 *     builder — diagonal pairs share a phase for natural trot.
 *   - Crawler segments undulate vertically with phase per `waveIndex`.
 *
 * Behaviour:
 *
 *   passive  — wander gently, occasionally pause.
 *   skittish — wander, flee when player approaches.
 *   hostile  — wander; chase + bite the player when in sight.
 *
 * Collision:
 *
 *   - Creatures register themselves as a `SurfaceObstacle` with live
 *     position so the FPS controller blocks the player from walking
 *     through them.
 *   - Hostile creatures damage the player on contact (with cooldown) and
 *     bounce back so they don't pin themselves inside the astronaut.
 *   - All creatures push themselves clear of the player every frame, so
 *     they never end up clipping inside the astronaut even if behaviour
 *     drives them into it.
 *
 * Terrain:
 *
 *   Y is set every frame from `terrain.getCollisionGroundY` plus a small
 *   `feetLift` margin (default 0.04 * scale) so the creature reads as
 *   standing on the surface rather than half-sunk into it on slopes.
 */
export class Creature extends Entity {
  readonly spec: FaunaSpec;
  /** Live obstacle entry — exposed so the spawner can register it with
   *  the FPS controller's obstacle list. Position is by reference, so
   *  the obstacle moves with the creature for free. */
  readonly obstacle: SurfaceObstacle;
  /** Body radius in world units. Drives the obstacle radius and the
   *  player-contact zone. */
  readonly bodyRadius: number;
  readonly bodyHeight: number;

  private readonly terrain: Terrain;
  private readonly getPlayerPosition: () => THREE.Vector3;
  private readonly boundaryRadius: number;
  private readonly playerRadius: number;
  private readonly stats: PlayerStats | null;
  private readonly mesh: CreatureMesh;
  private readonly attackDamage: number;

  private readonly velocity = new THREE.Vector3();
  private readonly targetDir = new THREE.Vector3();
  private speed = 0;
  private state: 'idle' | 'walk' | 'flee' | 'chase' = 'idle';
  private stateTimer = 0;
  private bobPhase = Math.random() * Math.PI * 2;
  private wingPhase = Math.random() * Math.PI * 2;
  private gaitPhase = Math.random() * Math.PI * 2;
  private attackCooldown = 0;

  constructor(options: CreatureOptions) {
    super();
    this.spec = options.spec;
    this.terrain = options.terrain;
    this.getPlayerPosition = options.getPlayerPosition;
    this.boundaryRadius = options.boundaryRadius;
    this.playerRadius = options.playerRadius ?? 0.36;
    this.stats = options.stats ?? null;

    const scale =
      options.spec.minScale +
      Math.random() * (options.spec.maxScale - options.spec.minScale);
    this.mesh = buildCreature(
      options.spec.archetype,
      scale,
      options.colors,
      options.spec.displayName
    );

    this.bodyRadius = this.mesh.bodyRadius;
    this.bodyHeight = this.mesh.bodyHeight;
    // Predator melee damage scales with size.
    this.attackDamage =
      options.spec.temperament === 'hostile'
        ? 6 + Math.round(scale * 4)
        : 0;

    this.object3D.add(this.mesh.root);
    this.object3D.position.copy(options.position);
    this.object3D.rotation.y = Math.random() * Math.PI * 2;
    this.object3D.name = `Creature:${options.spec.displayName}`;

    // Live obstacle — `position` is the creature's own Vector3 reference,
    // so the FPS controller's collision check sees the current position
    // every frame without us having to push updates.
    this.obstacle = {
      position: this.object3D.position,
      radius: this.bodyRadius,
      height: this.bodyHeight
    };

    this.pickWanderDir();
  }

  override update(dt: number): void {
    this.stateTimer -= dt;
    this.bobPhase += dt;
    this.wingPhase += dt * 6;

    const player = this.getPlayerPosition();
    const dx = player.x - this.object3D.position.x;
    const dz = player.z - this.object3D.position.z;
    const distSq = dx * dx + dz * dz;

    this.updateBehaviour(distSq, dx, dz);
    this.updateMotion(dt);
    this.resolveContactWithPlayer(dt);
    this.updateAnimation(dt);

    if (this.attackCooldown > 0) this.attackCooldown -= dt;
  }

  private updateBehaviour(distSq: number, dx: number, dz: number): void {
    const FLEE_RANGE_SQ = 22 * 22;
    const CHASE_RANGE_SQ = 32 * 32;

    if (this.spec.temperament === 'hostile') {
      if (distSq < CHASE_RANGE_SQ) {
        this.state = 'chase';
        const len = Math.max(Math.sqrt(distSq), 0.001);
        this.targetDir.set(dx / len, 0, dz / len);
        this.speed = this.archetypeSpeed() * 1.4;
        return;
      }
    } else if (this.spec.temperament === 'skittish') {
      if (distSq < FLEE_RANGE_SQ) {
        this.state = 'flee';
        const len = Math.max(Math.sqrt(distSq), 0.001);
        this.targetDir.set(-dx / len, 0, -dz / len);
        this.speed = this.archetypeSpeed() * 1.6;
        return;
      }
    }

    if (this.stateTimer > 0) return;

    if (this.state === 'idle') {
      this.state = 'walk';
      this.stateTimer = 3 + Math.random() * 4;
      this.pickWanderDir();
      this.speed = this.archetypeSpeed();
    } else {
      this.state = 'idle';
      this.stateTimer = 1.5 + Math.random() * 2.5;
      this.speed = 0;
    }
  }

  private archetypeSpeed(): number {
    switch (this.spec.archetype) {
      case 'crawler':
        return 1.4;
      case 'walker':
        return 2.4;
      case 'glider':
      case 'spectre':
        return 3.4;
      case 'predator':
        return 3.0;
    }
  }

  private updateMotion(dt: number): void {
    const desiredSpeed = this.speed;
    if (desiredSpeed > 0) {
      const steerX = this.targetDir.x * desiredSpeed - this.velocity.x;
      const steerZ = this.targetDir.z * desiredSpeed - this.velocity.z;
      const steerStep = Math.min(1, dt * 4);
      this.velocity.x += steerX * steerStep;
      this.velocity.z += steerZ * steerStep;
    } else {
      const decay = Math.max(0, 1 - dt * 5);
      this.velocity.x *= decay;
      this.velocity.z *= decay;
    }

    const pos = this.object3D.position;
    pos.x += this.velocity.x * dt;
    pos.z += this.velocity.z * dt;

    // Soft boundary — turn back inward when crossing the playable disc.
    const r = Math.hypot(pos.x, pos.z);
    if (r > this.boundaryRadius) {
      pos.x = (pos.x / r) * this.boundaryRadius;
      pos.z = (pos.z / r) * this.boundaryRadius;
      // Re-aim toward origin on next state.
      const ang = Math.atan2(-pos.x, -pos.z);
      this.targetDir.set(Math.sin(ang), 0, Math.cos(ang));
      this.velocity.set(this.targetDir.x * desiredSpeed, 0, this.targetDir.z * desiredSpeed);
    }

    // Vertical placement — ground walking vs hovering. The collision
    // floor (water) is honoured so creatures walk on lakes the same way
    // the player does.
    const ground = this.terrain.getCollisionGroundY(pos.x, pos.z);
    if (this.spec.archetype === 'glider' || this.spec.archetype === 'spectre') {
      const hover = this.spec.hoverHeight ?? 8;
      const wobble =
        Math.sin(this.bobPhase * 0.8) * 0.6 +
        Math.sin(this.bobPhase * 1.7) * 0.3;
      pos.y = ground + hover + wobble;
    } else {
      // Tiny lift so legs/feet stay above the visible mesh on uneven
      // ground. Creature scale doesn't matter much here — 0.05u is a
      // hair above the polygon plane.
      pos.y = ground + 0.05;
    }

    // Yaw — align local +X with velocity (creatures are built with head
    // pointing along +X; rotation.y of θ takes +X to (cos θ, 0, -sin θ),
    // so θ = atan2(-vz, vx)).
    if (this.velocity.lengthSq() > 0.05) {
      const targetYaw = Math.atan2(-this.velocity.z, this.velocity.x);
      const cur = this.object3D.rotation.y;
      this.object3D.rotation.y = lerpAngle(cur, targetYaw, Math.min(1, dt * 5));
    }
  }

  /** Push the creature out of the player's collision radius and apply
   *  damage on contact for hostile species. The creature resolves its own
   *  side of the collision — the FPS controller resolves the player's
   *  side via the registered `obstacle`. */
  private resolveContactWithPlayer(dt: number): void {
    void dt;
    const player = this.getPlayerPosition();
    const dx = this.object3D.position.x - player.x;
    const dz = this.object3D.position.z - player.z;
    const distSq = dx * dx + dz * dz;
    const minDist = this.bodyRadius + this.playerRadius;
    if (distSq >= minDist * minDist) return;
    const dist = Math.max(Math.sqrt(distSq), 0.001);

    // Push the creature out radially.
    const overlap = minDist - dist;
    const nx = dx / dist;
    const nz = dz / dist;
    this.object3D.position.x += nx * overlap;
    this.object3D.position.z += nz * overlap;

    // Kill any inward velocity component so the creature doesn't keep
    // pressing against the player.
    const inward = -(this.velocity.x * nx + this.velocity.z * nz);
    if (inward > 0) {
      this.velocity.x += inward * nx;
      this.velocity.z += inward * nz;
    }

    if (
      this.spec.temperament === 'hostile' &&
      this.attackCooldown <= 0 &&
      this.stats
    ) {
      this.stats.damageHealth(this.attackDamage);
      this.attackCooldown = 0.8;
      // Stagger backward so the next attack requires a fresh approach.
      this.velocity.x += nx * 8;
      this.velocity.z += nz * 8;
    }
  }

  private updateAnimation(dt: number): void {
    void dt;
    const moving = this.velocity.lengthSq() > 0.1;
    // Gait advance — frequency scales with speed so faster creatures step
    // faster.
    const speedRatio = Math.min(this.velocity.length() / this.archetypeSpeed(), 1.4);
    this.gaitPhase += dt * (4.0 + 5.0 * speedRatio);

    // Walker / predator legs — swing around local Z (forward/back).
    const legs = this.mesh.parts.legs;
    const segments = this.mesh.parts.segments;
    const isLeggy =
      this.spec.archetype === 'walker' || this.spec.archetype === 'predator';

    if (isLeggy && legs.length > 0) {
      const swing = moving ? 0.5 : 0.05;
      for (const leg of legs) {
        const phase = (leg.userData['gaitPhase'] as number) ?? 0;
        leg.rotation.z = Math.sin(this.gaitPhase + phase) * swing;
      }
    }

    // Crawler segments undulate vertically.
    if (segments && segments.length > 0) {
      const amp = moving ? 0.07 : 0.025;
      for (const seg of segments) {
        const idx = (seg.userData['waveIndex'] as number) ?? 0;
        const baseY = (seg.userData['baseY'] as number) ?? 0;
        seg.position.y = baseY + Math.sin(this.gaitPhase * 1.6 - idx * 0.7) * amp;
      }
    }

    // Body bob — tiny up/down on the body mesh while walking.
    const body = this.mesh.parts.body;
    if (body && isLeggy) {
      const bob = Math.sin(this.gaitPhase * 2) * (moving ? 0.04 : 0.01);
      const baseY = (body.userData['baseY'] as number | undefined) ?? body.position.y;
      if (!('baseY' in body.userData)) body.userData['baseY'] = baseY;
      body.position.y = baseY + bob;
    }

    // Wings — flap continuously for hovering archetypes.
    for (const w of this.mesh.parts.wings) {
      w.rotation.x = Math.sin(this.wingPhase) * 0.45;
    }
  }

  private pickWanderDir(): void {
    const angle = Math.random() * Math.PI * 2;
    this.targetDir.set(Math.sin(angle), 0, Math.cos(angle));
  }
}

function lerpAngle(a: number, b: number, t: number): number {
  let diff = b - a;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  return a + diff * Math.min(1, Math.max(0, t));
}
